import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  OAuthProvider,
  getMultiFactorResolver,
  multiFactor,
  TotpMultiFactorGenerator,
  type User,
  type MultiFactorError,
  type MultiFactorResolver,
  type TotpSecret,
} from 'firebase/auth';
import { Navigate, useLocation } from 'react-router-dom';
import { api } from '../api';

// ── Firebase init ─────────────────────────────────────────────
// Initialised once at module load from VITE_ env vars.
// Both apps (client + supplier) import this module, which is why
// the init is centralised here.

function initFirebase(): FirebaseApp {
  if (getApps().length > 0) return getApps()[0]!;
  const env = (import.meta as { env: Record<string, string> }).env;
  return initializeApp({
    apiKey: env.VITE_FIREBASE_API_KEY,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
  });
}

initFirebase();

// ── Types ─────────────────────────────────────────────────────

export type UserKind = 'client' | 'supplier';

/** Mirrors the backend Capability enum. Capability — not role — drives every permission gate. */
export type Capability =
  | 'VIEW_ALL'
  | 'COMMERCIAL'
  | 'MANAGE_ORG';

export interface AppProfile {
  id: string;
  firebaseUid: string;
  email: string;
  fullName: string;
  avatarUrl: string | null;
  phone: string | null;
  userKind: UserKind;
  /** Effective capabilities from the user's teams — the single source for UI permission gates. */
  capabilities: Capability[];
  language: string | null;
  organizationId: string;
  supplierId: string | null;
  organization: {
    id: string;
    name: string;
    logoUrl: string | null;
    defaultCurrency: string;
    defaultLanguage: string;
  } | null;
}

/** Result of a password sign-in: when `mfaRequired`, complete it via `completeMfaChallenge`. */
export interface MfaSignInResult {
  mfaRequired: boolean;
}

interface AuthState {
  /** Firebase user — null = signed out, undefined = loading */
  firebaseUser: User | null | undefined;
  /** Our DB profile, populated after sign-in */
  profile: AppProfile | null;
  /** Alias for profile — use profile.fullName, profile.capabilities, etc. */
  user: AppProfile | null;
  signIn: (email: string, password: string) => Promise<MfaSignInResult>;
  /** Microsoft (Entra ID) SSO. Multi-tenant: any client's work/school account can authenticate. */
  signInWithMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * True when a valid credential authenticated but belongs to the OTHER portal (e.g. a supplier
   * account on the client app). The session was rejected and signed out; the login screen should
   * surface a "wrong portal" message.
   */
  wrongApp: boolean;

  // ── MFA (TOTP) ──────────────────────────────────────────────
  /** True while a password sign-in is awaiting a TOTP code (challenge in progress). */
  mfaChallengePending: boolean;
  /** Completes a pending TOTP sign-in challenge with a code from the authenticator app. */
  completeMfaChallenge: (code: string) => Promise<void>;
  /** Abandons a pending MFA challenge (e.g. the user navigates away). */
  cancelMfaChallenge: () => void;
  /** True when the signed-in user authenticated with email/password (vs. an SSO provider). */
  isPasswordUser: boolean;
  /**
   * Changes the signed-in password user's password. Firebase requires a recent login, so the
   * current password is re-verified first (reauthenticate) before the update. Password users only.
   */
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  /** True when the signed-in user has at least one enrolled second factor. */
  mfaEnrolled: boolean;
  /** True when the signed-in password user has not yet enrolled a second factor. */
  needsMfaEnrollment: boolean;
  /** Begins TOTP enrollment; returns the shared secret + an otpauth:// URL for a QR code. */
  startTotpEnrollment: () => Promise<{ secretKey: string; qrCodeUrl: string }>;
  /** Finishes TOTP enrollment with a code from the authenticator app. */
  finishTotpEnrollment: (code: string) => Promise<void>;
}

// ── Context ───────────────────────────────────────────────────

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/**
 * Capability-based permission gates for the UI. Access is team-based: the backend
 * is the source of truth and returns the caller's effective capabilities (resolved
 * from their team memberships) on the profile.
 */
export function usePermissions() {
  const { user } = useAuth();
  const caps = user?.capabilities ?? [];
  const can = (c: Capability) => caps.includes(c);
  return {
    can,
    canManageOrg: can('MANAGE_ORG'),
    /** Sees every team's records (purchase/admin). Absence = own-team only (RLS). */
    canViewAll: can('VIEW_ALL'),
    /** Runs the commercial lifecycle: dispatch, sign, award, terminal moves (purchase/admin). */
    canCommercial: can('COMMERCIAL'),
  };
}

// ── AuthProvider ──────────────────────────────────────────────

interface AuthProviderProps {
  app: 'client' | 'supplier';
  children: ReactNode;
}

export function AuthProvider({ app, children }: AuthProviderProps) {
  const auth = getAuth();
  const [firebaseUser, setFirebaseUser] = useState<User | null | undefined>(undefined);
  const [profile, setProfile] = useState<AppProfile | null>(null);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  // True after a credential authenticated against the wrong portal (e.g. a supplier on the client
  // app). Both apps share one Firebase project, so the credential itself is valid — we can't stop
  // it authenticating, but we refuse to establish a session and sign it straight back out.
  const [wrongApp, setWrongApp] = useState(false);
  // Holds the TOTP secret between starting and finishing enrollment (not state — never rendered).
  const totpSecretRef = useRef<TotpSecret | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);
      if (!user) {
        setProfile(null);
        return;
      }
      try {
        const data = await api.get<AppProfile>('/api/auth/me');
        // Hard portal gate: this account belongs to the other app. Reject the session and sign out;
        // signing out re-fires this listener with user=null, leaving the login screen in place.
        if (data.userKind !== app) {
          setWrongApp(true);
          setProfile(null);
          await firebaseSignOut(auth);
          return;
        }
        setWrongApp(false);
        setProfile(data);
      } catch {
        setProfile(null);
      }
    });
  }, [auth, app]);

  const signIn = async (email: string, password: string): Promise<MfaSignInResult> => {
    setWrongApp(false);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // onAuthStateChanged fires and loads the profile
      return { mfaRequired: false };
    } catch (e) {
      // An enrolled user is challenged for a second factor instead of being signed in.
      if ((e as { code?: string })?.code === 'auth/multi-factor-auth-required') {
        setMfaResolver(getMultiFactorResolver(auth, e as MultiFactorError));
        return { mfaRequired: true };
      }
      throw e;
    }
  };

  const completeMfaChallenge = async (code: string) => {
    if (!mfaResolver) throw new Error('No MFA challenge in progress');
    // We only enroll TOTP, so use the TOTP hint (fall back to the first hint defensively).
    const hint =
      mfaResolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID) ??
      mfaResolver.hints[0];
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
    await mfaResolver.resolveSignIn(assertion);
    setMfaResolver(null);
    // onAuthStateChanged fires and loads the profile
  };

  const cancelMfaChallenge = () => setMfaResolver(null);

  // A password user with zero enrolled factors must enroll before using the app. SSO users
  // (provider microsoft.com) are exempt — their MFA is the identity provider's responsibility.
  const isPasswordUser =
    !!firebaseUser && firebaseUser.providerData.some((p) => p.providerId === 'password');
  const mfaEnrolled =
    !!firebaseUser && multiFactor(firebaseUser).enrolledFactors.length > 0;
  const needsMfaEnrollment = isPasswordUser && !mfaEnrolled;

  const startTotpEnrollment = async () => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    const session = await multiFactor(user).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(session);
    totpSecretRef.current = secret;
    return {
      secretKey: secret.secretKey,
      qrCodeUrl: secret.generateQrCodeUrl(user.email ?? 'account', 'Nexdel'),
    };
  };

  const finishTotpEnrollment = async (code: string) => {
    const user = auth.currentUser;
    const secret = totpSecretRef.current;
    if (!user || !secret) throw new Error('No enrollment in progress');
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
    await multiFactor(user).enroll(assertion, 'Authenticator app');
    totpSecretRef.current = null;
  };

  const signInWithMicrosoft = async () => {
    setWrongApp(false);
    const provider = new OAuthProvider('microsoft.com');
    // Multi-tenant SaaS: allow any organization's work/school account, not a single tenant.
    // Override with VITE_MICROSOFT_TENANT (a specific tenant id) if ever locking to one org.
    const env = (import.meta as { env: Record<string, string> }).env;
    provider.setCustomParameters({ tenant: env.VITE_MICROSOFT_TENANT ?? 'organizations' });
    await signInWithPopup(auth, provider);
    // onAuthStateChanged fires and loads the profile (or leaves it null for a not-yet-invited user)
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('Not signed in');
    // Firebase rejects updatePassword without a recent login — re-verify with the current password.
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
    setProfile(null);
  };

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        profile,
        user: profile,
        signIn,
        signInWithMicrosoft,
        signOut,
        wrongApp,
        mfaChallengePending: mfaResolver !== null,
        completeMfaChallenge,
        cancelMfaChallenge,
        isPasswordUser,
        changePassword,
        mfaEnrolled,
        needsMfaEnrollment,
        startTotpEnrollment,
        finishTotpEnrollment,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ── ProtectedRoute ────────────────────────────────────────────
// Redirects to /login if not authenticated.
// Shows nothing while Firebase is still initialising (firebaseUser = undefined).

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { firebaseUser } = useAuth();
  const location = useLocation();

  if (firebaseUser === undefined) {
    // Still loading — render nothing to avoid flash of login page
    return null;
  }

  if (firebaseUser === null) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// ── RequireCapability ─────────────────────────────────────────
// Route guard for back-office sections (suppliers/contacts/analytics → VIEW_ALL, settings → MANAGE_ORG).
// UX only — the backend enforces the same on every endpoint. A user who guesses the URL is bounced home.

export function RequireCapability({ capability, children }: { capability: Capability; children: ReactNode }) {
  const { firebaseUser, profile } = useAuth();
  const { can } = usePermissions();

  // Still loading the DB profile — capabilities aren't known yet. Render nothing rather
  // than bouncing an authorized user home on a deep link / refresh before the profile resolves.
  if (firebaseUser === undefined || (firebaseUser !== null && profile === null)) {
    return null;
  }

  if (!can(capability)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// The wrong-portal gate lives in AuthProvider (see `wrongApp`): a credential that authenticates
// against the wrong app is rejected and signed out centrally, so it can't matter which route the
// user lands on. Login screens read `wrongApp` from useAuth() to show the rejection message.
